import React from 'react';
import { Box, Typography } from '@mui/material';
import { formatInteger, formatNullableCurrency } from '../formatters';

/**
 * People-attribution rework S7 — decision D-J (owner, 2026-08-29): «wherever
 * pLTV is shown, CAC sits directly beside it» — an invariant, not a column
 * list. pLTV is unreadable without the number it must beat, in plain view
 * mode too. This is the ONE cell that renders a pLTV figure or watch-only
 * band; a grep-guard (pltvWithCac.guard.test.ts) fails if any other site
 * renders `WatchOnlyPltvChip` / `PltvCellContent` / `CreativeWatchOnlyPltvSection`
 * outside it. The CAC line uses the ONE wording (`cacLineText`) the Traffic
 * sources view uses, so card and panel agree to the character (AC-060).
 *
 * CAC here = spend ÷ newPayingPeople on the SAME journey set as the pLTV
 * (last click, served model) — never spend ÷ trials (S-1/S-2).
 */
export interface CacBeside {
  /** null = a real «—» with a reason; undefined = typed absence (refresh). */
  value: number | null | undefined;
  /** Denominator shown beside the price — people whose first paid journey is on this row. */
  count?: number;
  reason?: string;
}

export const CAC_REFRESH_REASON = 'people data needs refresh';

/** The ONE CAC wording: «CAC $240.00 · 5 new paying people» / «CAC n/a · reason». */
export function cacLineText(cac: CacBeside, currency = 'usd'): string {
  if (cac.value === undefined) return `CAC n/a · ${cac.reason ?? CAC_REFRESH_REASON}`;
  // S11 §14.3 #3: a typed absence still names its people («no campaign · 30
  // new paying people») — the scope denominator must be re-derivable by eye.
  if (cac.value === null) {
    const absentCount = typeof cac.count === 'number' && cac.count > 0 ? ` · ${formatInteger(cac.count)} new paying people` : '';
    return `CAC n/a · ${cac.reason ?? 'no paid conversions yet'}${absentCount}`;
  }
  const count = typeof cac.count === 'number' ? ` · ${formatInteger(cac.count)} new paying people` : '';
  return `CAC ${formatNullableCurrency(cac.value, currency)}${count}`;
}

/** Builds the CAC line input from any served row that carries the S2/S4 twins. */
export function cacBesideFromRow(row: { cac?: number | null; newPayingPeople?: number; peopleIdentityPlane?: 'linkage' | 'behavioural' }): CacBeside {
  if (row.peopleIdentityPlane === 'behavioural') return { value: null, reason: 'no ad account' };
  if (row.cac === undefined) return { value: undefined, reason: CAC_REFRESH_REASON };
  if (row.cac === null) {
    return typeof row.newPayingPeople === 'number' && row.newPayingPeople === 0
      ? { value: null, reason: 'no paid conversions yet', count: 0 }
      : { value: null, reason: 'no spend on this row' };
  }
  return { value: row.cac, count: row.newPayingPeople };
}

export function PltvWithCac({ pltv, cac, currency, testId }: {
  /** The pLTV figure / chip / band — rendered as given. */
  pltv: React.ReactNode;
  cac: CacBeside;
  currency?: string;
  /** Test id of the CAC line (the pLTV node keeps its own). */
  testId?: string;
}): React.ReactElement {
  const text = cacLineText(cac, currency);
  return (
    <Box>
      {pltv}
      <Typography
        variant="caption"
        color={cac.value === undefined || cac.value === null ? 'text.disabled' : 'text.secondary'}
        sx={{ display: 'block', fontSize: '0.68rem', mt: 0.25 }}
        data-testid={testId}
        title="Spend ÷ people whose FIRST paid conversion is credited to this row (last click, the served model). Compare with the pLTV above: pLTV must beat CAC."
      >
        {text}
      </Typography>
    </Box>
  );
}
